import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import {
  ApolloCache,
  DefaultContext,
  MutationTuple,
  OperationVariables,
  useMutation,
} from "@apollo/client";
import { logEvent } from "../../../helpers/analytics";
import {
  CompanyInformationFormData,
  ContactDetailsFormData,
  CommunityInformationFormData,
  CommunityDemoRequestFormData,
  DemoPreferenceFormData,
} from "../schemas/schema";
import { gql } from "../../../__generated__";
import {
  CreateCommunityManagementDemoRequestTaskMutation,
  CommunityManagementDemoRequestDto,
  Exact,
} from "../../../__generated__/graphql";

export const CREATE_COMMUNITY_DEMO_REQUEST = gql(`
  mutation CreateCommunityManagementDemoRequestTask($communityManagementDemoRequestDto: CommunityManagementDemoRequestDto!) {
    createCommunityManagementDemoRequestTask(communityManagementDemoRequestDto: $communityManagementDemoRequestDto)
  }
`);

interface CommunityDemoRequestContextType {
  currentStep: number;
  isLoading: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  submitForm: MutationTuple<
    CreateCommunityManagementDemoRequestTaskMutation,
    Exact<{ communityManagementDemoRequestDto: CommunityManagementDemoRequestDto }>,
    DefaultContext,
    ApolloCache<any>
  >[0];
  resetForm: () => void;
  updateCompanyInfo: (data: CompanyInformationFormData) => void;
  updateContactDetails: (data: ContactDetailsFormData) => void;
  updateCommunityInfo: (data: CommunityInformationFormData) => void;
  updateDemoPreference: (data: DemoPreferenceFormData) => void;
  getAllFormData: () => Partial<CommunityDemoRequestFormData>;
  getCompanyInfo: () => Partial<CompanyInformationFormData>;
  getContactDetails: () => Partial<ContactDetailsFormData>;
  getCommunityInfo: () => Partial<CommunityInformationFormData>;
  getDemoPreference: () => Partial<DemoPreferenceFormData>;
}

const CommunityDemoRequestContext =
  createContext<CommunityDemoRequestContextType | undefined>(undefined);

export const useCommunityDemoRequest = (): CommunityDemoRequestContextType => {
  const context = useContext(CommunityDemoRequestContext);
  if (!context) {
    throw new Error(
      "useCommunityDemoRequest must be used within a CommunityDemoRequestProvider"
    );
  }
  return context;
};

export const CommunityDemoRequestProvider = ({
  children,
}: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<
    Partial<CommunityDemoRequestFormData>
  >({});

  const [createCommunityDemoRequestTask, { loading }] = useMutation(
    CREATE_COMMUNITY_DEMO_REQUEST
  );

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
      logEvent("community_demo_request_step_changed", { step });
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
      logEvent("community_demo_request_next_step", { step: currentStep + 1 });
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      logEvent("community_demo_request_previous_step", {
        step: currentStep - 1,
      });
    }
  }, [currentStep]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData({});
    logEvent("community_demo_request_reset");
  }, []);

  const updateCompanyInfo = useCallback((data: CompanyInformationFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_demo_request_company_info_updated");
  }, []);

  const updateContactDetails = useCallback((data: ContactDetailsFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_demo_request_contact_details_updated");
  }, []);

  const updateCommunityInfo = useCallback(
    (data: CommunityInformationFormData) => {
      setFormData((prev) => ({ ...prev, ...data }));
      logEvent("community_demo_request_community_info_updated");
    },
    []
  );

  const updateDemoPreference = useCallback((data: DemoPreferenceFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_demo_request_demo_preference_updated");
  }, []);

  const getAllFormData = useCallback(() => formData, [formData]);

  const getCompanyInfo = useCallback((): Partial<CompanyInformationFormData> => {
    const { organization, roleInOrganization } = formData;
    return { organization, roleInOrganization };
  }, [formData]);

  const getContactDetails = useCallback((): Partial<ContactDetailsFormData> => {
    const { email, phone } = formData;
    return { email, phone };
  }, [formData]);

  const getCommunityInfo = useCallback(
    (): Partial<CommunityInformationFormData> => {
      const {
        typeOfCommunity,
        communitySize,
        communityTalent,
        majorChallenges,
        digitalToolsUsed,
      } = formData as CommunityDemoRequestFormData;
      return {
        typeOfCommunity,
        communitySize,
        communityTalent,
        majorChallenges,
        digitalToolsUsed,
      };
    },
    [formData]
  );

  const getDemoPreference = useCallback(
    (): Partial<DemoPreferenceFormData> => {
      const { preferredDate, preferredTime } = formData as CommunityDemoRequestFormData;
      return { preferredDate, preferredTime };
    },
    [formData]
  );



  const contextValue: CommunityDemoRequestContextType = useMemo(
    () => ({
      currentStep,
      isLoading: loading,
      goToStep,
      nextStep,
      previousStep,
      submitForm: createCommunityDemoRequestTask,
      resetForm,
      updateCompanyInfo,
      updateContactDetails,
      updateCommunityInfo,
      updateDemoPreference,
      getAllFormData,
      getCompanyInfo,
      getContactDetails,
      getCommunityInfo,
      getDemoPreference,
    }),
    [
      currentStep,
      loading,
      goToStep,
      nextStep,
      previousStep,
      createCommunityDemoRequestTask,
      resetForm,
      updateCompanyInfo,
      updateContactDetails,
      updateCommunityInfo,
      updateDemoPreference,
      getAllFormData,
      getCompanyInfo,
      getContactDetails,
      getCommunityInfo,
      getDemoPreference,
    ]
  );

  return (
    <CommunityDemoRequestContext.Provider value={contextValue}>
      {children}
    </CommunityDemoRequestContext.Provider>
  );
};


